import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { FaBuilding, FaCalendarAlt, FaHardHat, FaMapMarkerAlt } from 'react-icons/fa';
import { ARCANDdata } from '../../../../Data/Projects/Arcand/Data_Arcand';
import ProjectShowcasePage, { ProjectShowcaseMetaItem, ProjectShowcaseSlide } from '../Shared/ProjectShowcasePage';
import logo from '../../../../Assets/images/Logo/SIMDEV-white-circle.png';

export default function Headquarters() {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const isFrench = i18n.language.startsWith('fr');
    const project = isFrench ? ARCANDdata.fr : ARCANDdata.en;
    const heroImage = project.images[0]?.url ?? '';
    const storyImage = project.images[2]?.url ?? heroImage;

    const meta: ProjectShowcaseMetaItem[] = [
        {
            label: isFrench ? 'Ville' : 'City',
            value: project.city,
            icon: FaMapMarkerAlt
        },
        {
            label: isFrench ? 'Année' : 'Year',
            value: String(project.yearBuilt),
            icon: FaCalendarAlt
        },
        {
            label: isFrench ? 'Structure' : 'Structure',
            value: project.features[0],
            icon: FaBuilding
        },
        {
            label: isFrench ? 'Mandat' : 'Mandate',
            value: project.features[3],
            icon: FaHardHat
        }
    ];

    const slideTitles = isFrench
        ? [
            'Le siège social',
            'Les espaces de travail',
            'Le lounge et la cuisine',
            'La transformation intérieure',
            'Les détails architecturaux'
        ]
        : [
            'The head office',
            'Work environments',
            'The lounge and kitchen',
            'The interior transformation',
            'Architectural details'
        ];

    const slides: ProjectShowcaseSlide[] = project.images.slice(0, 5).map((slide, index) => ({
        image: slide.url,
        eyebrow: project.name,
        title: slideTitles[index] ?? project.name,
        description: project.content.desc[index] ?? project.content.desc[0]
    }));

    return (
        <ProjectShowcasePage
            theme="simdev"
            logo={logo}
            logoAlt="SIMDEV"
            logoVariant="mark"
            heroImage={heroImage}
            heroAction={{
                label: isFrench ? 'Visiter le site' : 'View website',
                onClick: () => window.open(project.website, '_blank')
            }}
            heroCaption={project.city}
            introEyebrow={isFrench ? 'Siège social' : 'Head office'}
            title={project.name}
            subtitle={project.content.title}
            introLead={project.content.intro}
            introBody={[project.content.desc[0]]}
            meta={meta}
            storyEyebrow={isFrench ? 'Transformation' : 'Transformation'}
            storyTitle={
                isFrench
                    ? 'Un immeuble industriel transformé en environnement de travail contemporain.'
                    : 'An industrial building transformed into a contemporary work environment.'
            }
            storyBody={project.content.desc.slice(1, 4)}
            storyImage={storyImage}
            storyImageAlt={project.name}
            storyAction={{
                label: isFrench ? 'Découvrir SIMDEV' : 'Discover SIMDEV',
                onClick: () => window.open(project.website, '_blank')
            }}
            secondaryStoryAction={{
                label: isFrench ? 'Notre histoire' : 'Our story',
                onClick: () => navigate('/histoire')
            }}
            galleryEyebrow={isFrench ? 'Espaces' : 'Spaces'}
            galleryTitle={isFrench ? 'Le siège social prend plus de présence à l’écran' : 'The head office gets more presence on screen'}
            galleryDescription={
                isFrench
                    ? 'La galerie montre plus clairement les espaces de travail, les zones communes et la qualité générale de la rénovation.'
                    : 'The gallery shows the workspaces, shared areas, and overall renovation quality more clearly.'
            }
            slides={slides}
            previousSlideLabel={isFrench ? 'Image précédente' : 'Previous image'}
            nextSlideLabel={isFrench ? 'Image suivante' : 'Next image'}
            goToSlideLabel={isFrench ? "Aller à l'image" : 'Go to image'}
            featuresEyebrow={isFrench ? 'Faits saillants' : 'Highlights'}
            featuresTitle={isFrench ? 'Le projet Arcand en bref' : 'The Arcand project at a glance'}
            featuresLead={
                isFrench
                    ? "Le siège social reflète l'approche SIMDEV : une transformation précise, utile et tournée vers la qualité d’usage."
                    : 'The head office reflects the SIMDEV approach: precise, useful transformation built around quality of use.'
            }
            features={project.features}
            featuresConclusion={project.content.desc[4]}
            asideEyebrow="SIMDEV"
            asideTitle={project.name}
            asideSubtitle={project.address}
            asideBody={
                isFrench
                    ? 'La page HQ utilise maintenant le bon jeu de données Arcand et une structure plus large, plus cohérente avec le reste des pages projet.'
                    : 'The HQ page now uses the correct Arcand dataset and a wider structure that is more consistent with the rest of the project pages.'
            }
            asideAction={{
                label: isFrench ? 'Voir notre histoire' : 'See our story',
                onClick: () => navigate('/histoire')
            }}
        />
    );
}
